import type { Descendant } from 'slate';

import { Accordion, PlusIcon, classNames } from '@movingimage-evp/mi-ui-component-library';
import { type HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import { RichTextEditor } from '../../../../../components/rich-text-editor';
import { isRichTextEditorContentEmpty } from '../../../../../utils';
import { useUserPermissions } from '../../../../hooks/user-permissions';

import styles from './language-editors.module.css';

type Props = {
  language: string;
  preLiveDescription: Descendant[] | undefined;
  preLiveDescriptionChanged?: boolean;
  liveDescription: Descendant[] | undefined;
  liveDescriptionChanged?: boolean;
  postLiveDescription: Descendant[] | undefined;
  postLiveDescriptionChanged?: boolean;
  editorRefreshKeys: number[];
  disabled?: boolean;
  onDescriptionChange: (value: Descendant[], language: string, type: 'preLive' | 'live' | 'postLive') => void;
} & HTMLAttributes<HTMLDivElement>;

export function LanguageEditors({
  language,
  preLiveDescription,
  preLiveDescriptionChanged,
  liveDescription,
  liveDescriptionChanged,
  postLiveDescription,
  postLiveDescriptionChanged,
  editorRefreshKeys,
  onDescriptionChange,
  onBlur,
  className,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const { isEventEditingAllowed } = useUserPermissions();

  return (
    <div className={classNames(styles.editorsWrapper, className)}>
      <Accordion
        key={editorRefreshKeys[0]}
        data-testid={`prelive-event-description-accordion-${language}`}
        buttonId={`prelive-event-description-accordion-button-${language}`}
        compact
        isInitiallyOpen={!isRichTextEditorContentEmpty(JSON.stringify(preLiveDescription))}
        buttonLabel={t('manager.webcastSetup.eventDescription.labels.PRELIVE')}
        buttonContent={
          <>
            <PlusIcon />
            {t('manager.webcastSetup.eventDescription.labels.PRELIVE')}
            {preLiveDescriptionChanged && (
              <div className={styles.unsavedChanges} data-testid={`prelive-event-description-unsaved-hint-${language}`}>
                {t('manager.webcastSetup.eventDescription.unsavedChanges')}
              </div>
            )}
          </>
        }
      >
        <RichTextEditor
          key={language}
          data-testid={`prelive-event-description-rich-text-editor-${language}`}
          value={preLiveDescription}
          disabled={!isEventEditingAllowed || disabled}
          aria-disabled={!isEventEditingAllowed || disabled}
          className={styles.richTextEditor}
          onChange={(value) => onDescriptionChange(value, language, 'preLive')}
          onBlur={onBlur}
        />
      </Accordion>

      <Accordion
        key={editorRefreshKeys[1]}
        data-testid={`live-event-description-accordion-${language}`}
        buttonId={`live-event-description-accordion-button-${language}`}
        compact
        isInitiallyOpen={!isRichTextEditorContentEmpty(JSON.stringify(liveDescription))}
        buttonLabel={t('manager.webcastSetup.eventDescription.labels.LIVE')}
        buttonContent={
          <>
            <PlusIcon />
            {t('manager.webcastSetup.eventDescription.labels.LIVE')}
            {liveDescriptionChanged && (
              <div className={styles.unsavedChanges} data-testid={`live-event-description-unsaved-hint-${language}`}>
                {t('manager.webcastSetup.eventDescription.unsavedChanges')}
              </div>
            )}
          </>
        }
      >
        <RichTextEditor
          key={language}
          data-testid={`live-event-description-rich-text-editor-${language}`}
          value={liveDescription}
          disabled={!isEventEditingAllowed || disabled}
          aria-disabled={!isEventEditingAllowed || disabled}
          className={styles.richTextEditor}
          onChange={(value) => onDescriptionChange(value, language, 'live')}
          onBlur={onBlur}
        />
      </Accordion>

      <Accordion
        key={editorRefreshKeys[2]}
        data-testid={`postlive-event-description-accordion-${language}`}
        buttonId={`postlive-event-description-accordion-button-${language}`}
        compact
        isInitiallyOpen={!isRichTextEditorContentEmpty(JSON.stringify(postLiveDescription))}
        buttonLabel={t('manager.webcastSetup.eventDescription.labels.POSTLIVE')}
        buttonContent={
          <>
            <PlusIcon />
            {t('manager.webcastSetup.eventDescription.labels.POSTLIVE')}
            {postLiveDescriptionChanged && (
              <div
                className={styles.unsavedChanges}
                data-testid={`postlive-event-description-unsaved-hint-${language}`}
              >
                {t('manager.webcastSetup.eventDescription.unsavedChanges')}
              </div>
            )}
          </>
        }
      >
        <RichTextEditor
          key={language}
          data-testid={`postlive-event-description-rich-text-editor-${language}`}
          value={postLiveDescription}
          disabled={!isEventEditingAllowed || disabled}
          aria-disabled={!isEventEditingAllowed || disabled}
          className={styles.richTextEditor}
          onChange={(value) => onDescriptionChange(value, language, 'postLive')}
          onBlur={onBlur}
        />
      </Accordion>
    </div>
  );
}
